/**
 * Pure logic behind the pawning object desk — the metal and electronics/other rows an
 * operator fills in when pawning (Delphi `TAmanetareNew.ObjectDesk`).
 *
 * The same desk serves two workflows, exactly as in the legacy application: creating a
 * contract, and re-assessing a previous session's contract during session verification
 * (`VerificareAmanet`). Keeping the calculations here means both screens price an object
 * identically, which is the whole point of the verification being a second pawning.
 */
import {
    buildCustomMetalName,
    calculateUnitPriceFromReperpret,
    findMetalPriceReferenceForMetal,
    getKaratOptionsForMetal,
    type MetalKaratageOption,
    type MetalPriceOption,
} from './metalPriceOptions';

export const CUSTOM_METAL_CATEGORY = 5;
export const CUSTOM_METAL_ROW_COUNT = 2;
/** Delphi's stone-share list (UAmanetareNew.pas:1879-1886), adopted 2026-10-20 (BL-10). */
export const STONE_PERCENT_OPTIONS = [0, 5, 10, 20, 30, 40, 50];

export const ELECTRONICS_TYPE = 'Electronice';
export const OTHERS_TYPE = 'Altele';

export interface DeskMetalObject {
    id: string;
    enabled: boolean;
    denumire: string;
    tipMetal: string;
    karat: number;
    percent?: number;
    cantitate: number;
    um: string;
    pretUnitar: number;
    sumainLei: number;
    sumainEuro: number;
    piatraPercent: number;
    piatra: number;
    propus: number;
    px: boolean;
    categoria: number; // the price category (CAT I, II, III), or CUSTOM_METAL_CATEGORY
}

export interface DeskElectronicObject {
    id: string;
    enabled: boolean;
    denumire: string;
    cantitate: number;
    um: string;
    pretUnitar: number;
    sumainLei: number;
    sumainEuro: number;
    tip: string; // Electronice, Altele
}

export interface SingleObjectPrice {
    unitPrice: number;
    proposedValue: number;
    stoneWeight: number;
}

/** The metal codes the presets and the purity levels use, by the name the desk shows. */
const METAL_NAMES: Readonly<Record<string, string>> = {
    AU: 'AUR',
    AUR: 'AUR',
    AG: 'ARGINT',
    ARGINT: 'ARGINT',
    PT: 'PLATINA',
    PLATINA: 'PLATINA',
    PD: 'PALADIU',
    PALADIU: 'PALADIU',
};

/** The order the metals are shown in; any other metal follows, alphabetically. */
const METAL_ORDER = ['AUR', 'ARGINT', 'PLATINA', 'PALADIU'];

const metalName = (tipMetal: string): string => {
    const code = tipMetal.trim().toUpperCase();
    return METAL_NAMES[code] ?? code;
};

/** Metals in the desk's order: gold, silver, platinum, palladium, then any other by name. */
const compareMetals = (left: string, right: string): number => {
    const rank = (metal: string): number => {
        const position = METAL_ORDER.indexOf(metal);
        return position === -1 ? METAL_ORDER.length : position;
    };
    return rank(left) - rank(right) || left.localeCompare(right);
};

/** The price categories a preset can be pawned under: CAT I, II and III. */
const DESK_PRICE_CATEGORIES = new Set([1, 2, 3]);

/**
 * The configured presets the desk offers: those under CAT I, II or III. A preset without a price
 * category - `AUR585 CAT`, category 0 - is not pawned (owner, 2026-11-03). The desk grouped by
 * price category never showed it, as it had no section for it; grouped by metal it would.
 */
export const selectDeskPresets = (presets: DeskMetalObject[]): DeskMetalObject[] =>
    presets.filter(preset => DESK_PRICE_CATEGORIES.has(preset.categoria));

/** Whether a row is silver, which carries no stone fields. */
export const isSilverMetal = (tipMetal: string): boolean => metalName(tipMetal) === 'ARGINT';

/** One group of the metal desk: all rows of one metal and purity, or the free rows. */
export interface DeskMetalGroup {
    key: string;
    /** `AUR585`, `ARGINT925`; null for the free "other karats" rows. */
    title: string | null;
    /** The rows with their index in `metalObjects`, which every change handler takes. */
    rows: Array<{ obj: DeskMetalObject; index: number }>;
}

/**
 * The metal desk grouped by metal and purity (owner, 2026-11-03): AUR585 with its CAT I, II and
 * III, then AUR750, then ARGINT925, each group's rows by price category - and the free "other
 * karats" rows last, whatever metal is picked on them. It replaces a grouping by price category,
 * which put AUR750 CAT I under "CAT I (Au585)" and AUR585 CAT II under "CAT II (Au750)".
 */
export const groupDeskMetalObjects = (metalObjects: DeskMetalObject[]): DeskMetalGroup[] => {
    const freeRows: DeskMetalGroup['rows'] = [];
    const groups = new Map<string, { metal: string; purity: number; rows: DeskMetalGroup['rows'] }>();

    metalObjects.forEach((obj, index) => {
        if (obj.categoria === CUSTOM_METAL_CATEGORY) {
            freeRows.push({ obj, index });
            return;
        }
        const metal = metalName(obj.tipMetal);
        const key = `${metal}${obj.karat}`;
        const group = groups.get(key);
        if (group) {
            group.rows.push({ obj, index });
        } else {
            groups.set(key, { metal, purity: obj.karat, rows: [{ obj, index }] });
        }
    });

    const metalGroups = [...groups]
        .sort(([, a], [, b]) => compareMetals(a.metal, b.metal) || a.purity - b.purity)
        .map(([key, group]) => ({
            key,
            title: key,
            rows: [...group.rows].sort(
                (a, b) => a.obj.categoria - b.obj.categoria || a.index - b.index,
            ),
        }));

    return freeRows.length > 0
        ? [...metalGroups, { key: 'other-karats', title: null, rows: freeRows }]
        : metalGroups;
};

const blankMetalObject = (
    id: string,
    denumire: string,
    tipMetal: string,
    karat: number,
    categoria: number,
): DeskMetalObject => ({
    id,
    enabled: false,
    denumire,
    tipMetal,
    karat,
    cantitate: 0,
    um: 'gr',
    pretUnitar: 0,
    sumainLei: 0,
    sumainEuro: 0,
    piatraPercent: 0,
    piatra: 0,
    propus: 0,
    px: false,
    categoria,
});

export const createCustomMetalObject = (index: number): DeskMetalObject =>
    blankMetalObject(`metal_custom_${index}`, '', '', 0, CUSTOM_METAL_CATEGORY);

/** Tops the desk up to the fixed number of free "other karats" rows. */
export const appendCustomMetalRows = (metalObjects: DeskMetalObject[]): DeskMetalObject[] => {
    const customRows = metalObjects.filter(obj => obj.categoria === CUSTOM_METAL_CATEGORY);
    const missingRows = Array.from(
        { length: Math.max(CUSTOM_METAL_ROW_COUNT - customRows.length, 0) },
        (_unused, index) => createCustomMetalObject(customRows.length + index),
    );
    return [...metalObjects, ...missingRows];
};

/** Fallback desk used when no metal preset is configured. */
export const createDefaultMetalObjects = (): DeskMetalObject[] => {
    const presets: Array<[string, string, string, number, number]> = [
        ['metal_cat1', 'AUR585 CAT I', 'Aur', 585, 1],
        ['metal_cat2', 'AUR750 CAT II', 'Aur', 750, 2],
        ['metal_cat3', 'AUR585 CAT III', 'Aur', 585, 3],
        ['metal_silver', 'ARGINT925 CAT I', 'Argint', 925, 1],
    ];
    return presets.flatMap(([idPrefix, name, metal, purity, category]) =>
        [0, 1].map(index => blankMetalObject(`${idPrefix}_${index}`, name, metal, purity, category)),
    );
};

const blankElectronicObject = (id: string, tip: string): DeskElectronicObject => ({
    id,
    enabled: false,
    denumire: '',
    cantitate: 0,
    um: 'buc',
    pretUnitar: 0,
    sumainLei: 0,
    sumainEuro: 0,
    tip,
});

export const createDefaultElectronicObjects = (): DeskElectronicObject[] => [
    ...[0, 1, 2].map(index => blankElectronicObject(`electronic_${index}`, ELECTRONICS_TYPE)),
    ...[0, 1, 2].map(index => blankElectronicObject(`other_${index}`, OTHERS_TYPE)),
];

export const computeNetWeight = (quantity: number, stonePercent: number): number =>
    quantity * (1 - (stonePercent ?? 0) / 100);

export const isEligibleForPriceCalc = (
    obj: Pick<DeskMetalObject, 'enabled' | 'cantitate' | 'tipMetal' | 'karat'>,
): boolean => Boolean(obj.enabled && obj.cantitate > 0 && obj.tipMetal && obj.karat);

/** Applies one field edit to a metal row and the dependent stone, value and currency figures. */
export const applyLocalMetalCalculations = (
    base: DeskMetalObject,
    field: keyof DeskMetalObject,
    value: unknown,
    exchangeRate: number,
    apiCalculating: boolean,
): DeskMetalObject => {
    const updatedObj: DeskMetalObject = { ...base, [field]: value };

    // Keep the stone weight in sync when the gross weight or the stone percentage changes
    if (field === 'cantitate' || field === 'piatraPercent') {
        updatedObj.piatra = (updatedObj.cantitate * (updatedObj.piatraPercent ?? 0)) / 100;
    }

    if (apiCalculating) {
        return updatedObj;
    }

    if (field === 'sumainLei') {
        updatedObj.sumainEuro = Number(value) / exchangeRate;
        return updatedObj;
    }
    if (field === 'sumainEuro') {
        updatedObj.sumainLei = Number(value) * exchangeRate;
        return updatedObj;
    }

    const affectsPrice = field === 'cantitate' || field === 'pretUnitar' || field === 'piatraPercent';
    if (affectsPrice && updatedObj.pretUnitar > 0) {
        const proposedValue =
            computeNetWeight(updatedObj.cantitate, updatedObj.piatraPercent) * updatedObj.pretUnitar;
        updatedObj.propus = proposedValue;
        updatedObj.sumainLei = proposedValue;
        updatedObj.sumainEuro = proposedValue / exchangeRate;
    }

    return updatedObj;
};

/**
 * Merges a server price calculation into a metal row. The stone weight always follows the
 * server; the price only fills a row that has none yet, so an entered price is preserved.
 */
export const applySingleObjectPrice = (
    obj: DeskMetalObject,
    price: SingleObjectPrice,
    exchangeRate: number,
): DeskMetalObject => {
    if (obj.pretUnitar) {
        return { ...obj, piatra: price.stoneWeight };
    }
    const valueRon = computeNetWeight(obj.cantitate, obj.piatraPercent) * price.unitPrice;
    return {
        ...obj,
        piatra: price.stoneWeight,
        propus: price.proposedValue,
        pretUnitar: price.unitPrice,
        sumainLei: valueRon,
        sumainEuro: valueRon / exchangeRate,
    };
};

export interface CustomMetalPricing {
    priceOptions: readonly MetalPriceOption[];
    karatageOptions: readonly MetalKaratageOption[];
    extendedDuration: boolean;
    exchangeRate: number;
    apiCalculating: boolean;
}

/** Applies a metal/karat choice on an "other karats" row and prices it from `reperpret`. */
export const applyCustomMetalSelection = (
    obj: DeskMetalObject,
    selectedMetalType: string,
    selectedKarat: number,
    pricing: CustomMetalPricing,
): DeskMetalObject => {
    const karatOptions = getKaratOptionsForMetal(pricing.karatageOptions, selectedMetalType);
    const nextKarat =
        selectedKarat > 0 && karatOptions.includes(selectedKarat)
            ? selectedKarat
            : (karatOptions[0] ?? 0);
    const unitPrice = calculateUnitPriceFromReperpret(
        findMetalPriceReferenceForMetal(pricing.priceOptions, selectedMetalType, nextKarat),
        nextKarat,
        pricing.extendedDuration,
    );

    return applyLocalMetalCalculations(
        {
            ...obj,
            tipMetal: selectedMetalType,
            karat: nextKarat,
            denumire: buildCustomMetalName(selectedMetalType, nextKarat),
            pretUnitar: unitPrice,
        },
        'pretUnitar',
        unitPrice,
        pricing.exchangeRate,
        pricing.apiCalculating,
    );
};

/** Applies one field edit to an electronics/other row, keeping value and currencies in step. */
export const applyElectronicObjectChange = (
    obj: DeskElectronicObject,
    field: keyof DeskElectronicObject,
    value: unknown,
    exchangeRate: number,
): DeskElectronicObject => {
    const updatedObj: DeskElectronicObject = { ...obj, [field]: value };

    if (field === 'cantitate' || field === 'pretUnitar') {
        updatedObj.sumainLei = updatedObj.cantitate * updatedObj.pretUnitar;
        updatedObj.sumainEuro = updatedObj.sumainLei / exchangeRate;
    }
    if (field === 'sumainLei') {
        updatedObj.sumainEuro = Number(value) / exchangeRate;
    }
    if (field === 'sumainEuro') {
        updatedObj.sumainLei = Number(value) * exchangeRate;
    }

    return updatedObj;
};

/** Writes the electronics annex result (name and pawn price) back onto its row. */
export const applyElectronicsAnnex = (
    obj: DeskElectronicObject,
    objectName: string,
    pawnPrice: string,
    exchangeRate: number,
): DeskElectronicObject => {
    const unitPrice = Number.parseFloat(pawnPrice) || 0;
    const valueRon = obj.cantitate * unitPrice;
    return {
        ...obj,
        denumire: objectName,
        pretUnitar: unitPrice,
        sumainLei: valueRon,
        sumainEuro: valueRon / exchangeRate,
    };
};

export const sumEnabledObjectsLei = (
    metalObjects: readonly DeskMetalObject[],
    electronicObjects: readonly DeskElectronicObject[],
): number =>
    [...metalObjects, ...electronicObjects]
        .filter(obj => obj.enabled)
        .reduce((sum, obj) => sum + obj.sumainLei, 0);

/**
 * Builds the pawned-object description from the ticked rows, as Delphi's
 * `group_concat(denumire) / group_concat(round(Cantitate,2)) / group_concat(UM)` does.
 * Returns an empty string when nothing describable is ticked.
 */
export const buildPawnedObjectDescription = (
    metalObjects: readonly DeskMetalObject[],
    electronicObjects: readonly DeskElectronicObject[],
): string => {
    const metalParts = metalObjects
        .filter(obj => obj.enabled && obj.denumire && obj.cantitate > 0)
        .map(obj => {
            const netQuantity = Math.round(computeNetWeight(obj.cantitate, obj.piatraPercent) * 100) / 100;
            return `${obj.denumire} ${netQuantity} ${obj.um ?? 'gr'}`;
        });
    const electronicParts = electronicObjects
        .filter(obj => obj.enabled && obj.denumire && obj.cantitate > 0)
        .map(obj => `${obj.denumire} ${obj.cantitate} ${obj.um ?? 'buc'}`);
    return [...metalParts, ...electronicParts].join(', ');
};

/** Ticked electronics/other rows that were left without a name. */
export const hasUnnamedElectronicObjects = (
    electronicObjects: readonly DeskElectronicObject[],
): boolean => electronicObjects.some(obj => obj.enabled && !obj.denumire.trim());
